import React, { useEffect, useState, useCallback } from 'react';
import '../super-admin.css';
import { 
  Typography, Card, Form, Input, Button, message, Row, Col, 
  Statistic, Switch, InputNumber, Divider, Space, Tag, Table, Modal, Select, Popconfirm
} from 'antd';
import { 
  ApiOutlined, CreditCardOutlined, 
  SecurityScanOutlined, ReloadOutlined, SaveOutlined,
  KeyOutlined, GlobalOutlined, PlusOutlined, DeleteOutlined, UserOutlined, StopOutlined
} from '@ant-design/icons';
import { API_BASE as apiBase, authHeaders } from '../../config/api';

const { Title, Text } = Typography;

const PlatformSettings = () => {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    stripe: {
      publishable_key: 'pk_test_demo_key_123',
      secret_key: 'sk_test_demo_key_456',
      webhook_secret: 'whsec_demo_webhook_789'
    },
    api: {
      rate_limit_default: 500,
      rate_limit_window: 60, // minutes
      hmac_enabled: true,
      cors_origins: '*',
      ip_whitelist_enabled: false
    },
    integrations: {
      zoho_webhook_url: '',
      trello_webhook_url: '',
      joomla_plugin_enabled: true
    }
  });
  const [stats, setStats] = useState({
    total_api_keys: 0,
    active_subscriptions: 0,
    monthly_api_calls: 0,
    total_revenue: 0
  });
  const [form] = Form.useForm();
  
  // API Key Management State
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [keyName, setKeyName] = useState('');

  // Load platform settings
  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/admin/platform/settings`, {
        headers: { ...authHeaders() }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        form.setFieldsValue(data);
      }
    } catch (err) {
      console.error('Error loading platform settings:', err);
    } finally {
      setLoading(false);
    }
  }, [form]);

  // Load platform statistics
  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/admin/platform/stats`, {
        headers: { ...authHeaders() }
      });
      
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error loading platform stats:', err);
    }
  }, []);

  // Save settings
  const saveSettings = async (values) => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/admin/platform/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify(values)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to save settings');
      }

      message.success('Platform settings saved successfully');
      setSettings(values);
    } catch (err) {
      message.error(err.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  // Permanently delete revoked API key
  const handleDeleteKey = async (keyId) => {
    setApiKeysLoading(true);
    try {
      const res = await fetch(`${apiBase}/admin/api-keys/${keyId}/permanent`, {
        method: 'DELETE',
        headers: { ...authHeaders() }
      });

      if (res.ok) {
        message.success('API key deleted permanently');
        loadApiKeys();
        loadStats();
      } else {
        const error = await res.json();
        message.error(error.error || 'Failed to delete API key');
      }
    } catch (err) {
      message.error('Failed to delete API key');
    } finally {
      setApiKeysLoading(false);
    }
  };

  // Load API keys
  const loadApiKeys = useCallback(async () => {
    setApiKeysLoading(true);
    try {
      const res = await fetch(`${apiBase}/admin/api-keys`, {
        headers: { ...authHeaders() }
      });
      
      if (res.ok) {
        const data = await res.json();
        setApiKeys(data.keys || []);
      }
    } catch (err) {
      console.error('Error loading API keys:', err);
    } finally {
      setApiKeysLoading(false);
    }
  }, []);

  // Load users for dropdown
  const loadUsers = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/users?limit=1000`, {
        headers: { ...authHeaders() }
      });
      
      if (res.ok) {
        const data = await res.json();
        setUsers(data.items || []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  }, []);

  // Generate API key for user
  const handleGenerateKey = async () => {
    if (!selectedUser || !keyName) {
      message.error('Please select a user and enter a key name');
      return;
    }

    setApiKeysLoading(true);
    try {
      const res = await fetch(`${apiBase}/admin/api-keys/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify({
          user_id: selectedUser,
          name: keyName
        })
      });

      if (res.ok) {
        const data = await res.json();
        setGeneratedKey(data);
        message.success(`API key generated for ${data.user_name}`);
        loadApiKeys();
        loadStats();
      } else {
        const error = await res.json();
        message.error(error.error || 'Failed to generate API key');
      }
    } catch (err) {
      message.error('Failed to generate API key');
    } finally {
      setApiKeysLoading(false);
    }
  };

  // Revoke API key
  const handleRevokeKey = async (keyId) => {
    setApiKeysLoading(true);
    try {
      const res = await fetch(`${apiBase}/admin/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { ...authHeaders() }
      });

      if (res.ok) {
        message.success('API key revoked successfully');
        loadApiKeys();
        loadStats();
      } else {
        const error = await res.json();
        message.error(error.error || 'Failed to revoke API key');
      }
    } catch (err) {
      message.error('Failed to revoke API key');
    } finally {
      setApiKeysLoading(false);
    }
  };

  // Open generate modal
  const openGenerateModal = () => {
    setGenerateModalVisible(true);
    setGeneratedKey(null);
    setSelectedUser(null);
    setKeyName('');
    loadUsers();
  };

  // Close generate modal
  const closeGenerateModal = () => {
    setGenerateModalVisible(false);
    setGeneratedKey(null);
    setSelectedUser(null);
    setKeyName('');
  };

  useEffect(() => {
    loadSettings();
    loadStats();
    loadApiKeys();
  }, [loadSettings, loadStats, loadApiKeys]);

  const handleRefresh = () => {
    loadSettings();
    loadStats();
    loadApiKeys();
  };

  return (
    <div className="sa-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>Platform Settings</Title>
        <Button 
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={loading}
        >
          Refresh
        </Button>
      </div>

      {/* API Key Management Section */}
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <KeyOutlined style={{ color: '#3730a3' }} />
              <span>API Key Management</span>
            </div>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={openGenerateModal}
            >
              Generate Key for User
            </Button>
          </div>
        }
        style={{ borderRadius: 14, marginBottom: 24 }}
      >
        <Table
          dataSource={apiKeys}
          loading={apiKeysLoading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          columns={[
            {
              title: 'User',
              key: 'user',
              render: (_, record) => (
                <div>
                  <div style={{ fontWeight: 500 }}>{record.user_name}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{record.user_email}</div>
                  {record.company_name && (
                    <Tag color="blue" style={{ marginTop: 4 }}>{record.company_name}</Tag>
                  )}
                </div>
              )
            },
            {
              title: 'Key Name',
              dataIndex: 'key_name',
              key: 'key_name'
            },
            {
              title: 'Masked Key',
              dataIndex: 'masked_key',
              key: 'masked_key',
              render: (text) => <code style={{ fontSize: 12 }}>{text}</code>
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              render: (status) => (
                <Tag color={status === 'active' ? 'green' : 'red'}>
                  {status}
                </Tag>
              )
            },
            {
              title: 'Created',
              dataIndex: 'created_at',
              key: 'created_at',
              render: (date) => new Date(date).toLocaleDateString()
            },
            {
              title: 'Last Used',
              dataIndex: 'last_used_at',
              key: 'last_used_at',
              render: (date) => date ? new Date(date).toLocaleDateString() : 'Never'
            },
            {
              title: 'Action',
              key: 'action',
              render: (_, record) => {
                const revokeButton = (
                  <Button
                    type="link"
                    danger
                    icon={<StopOutlined />}
                    disabled={record.status !== 'active'}
                  >
                    Revoke
                  </Button>
                );

                const deleteButton = (
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={record.status !== 'revoked'}
                  >
                    Delete
                  </Button>
                );

                return (
                  <Space size="small">
                    {record.status === 'active' ? (
                      <Popconfirm
                        title="Revoke this API key?"
                        description="This action cannot be undone."
                        onConfirm={() => handleRevokeKey(record.id)}
                        okText="Yes, Revoke"
                        cancelText="Cancel"
                      >
                        {revokeButton}
                      </Popconfirm>
                    ) : (
                      revokeButton
                    )}

                    {record.status === 'revoked' ? (
                      <Popconfirm
                        title="Delete this API key permanently?"
                        description="This cannot be undone."
                        onConfirm={() => handleDeleteKey(record.id)}
                        okText="Yes, Delete"
                        cancelText="Cancel"
                      >
                        {deleteButton}
                      </Popconfirm>
                    ) : (
                      deleteButton
                    )}
                  </Space>
                );
              }
            }
          ]}
        />
      </Card>

      {/* Platform Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6} lg={6} xl={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="API Keys"
              value={stats.total_api_keys}
              prefix={<KeyOutlined style={{ color: '#3730a3' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={6} xl={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Active Subscriptions"
              value={stats.active_subscriptions}
              prefix={<CreditCardOutlined style={{ color: '#059669' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={6} xl={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Monthly API Calls"
              value={stats.monthly_api_calls}
              prefix={<ApiOutlined style={{ color: '#d97706' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={6} xl={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Total Revenue"
              value={stats.total_revenue}
              prefix="$"
              precision={2}
              valueStyle={{ color: '#7c3aed' }}
            />
          </Card>
        </Col>
      </Row>

      <Form
        form={form}
        layout="vertical"
        onFinish={saveSettings}
        initialValues={settings}
      >
        <Row gutter={[24, 24]}>
          {/* Stripe Configuration */}
          <Col xs={24} lg={12}>
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CreditCardOutlined style={{ color: '#635bff' }} />
                  <span>Stripe Configuration</span>
                  <Tag color="orange">Demo Mode</Tag>
                </div>
              }
              style={{ borderRadius: 14 }}
            >
              <Form.Item
                label="Publishable Key"
                name={['stripe', 'publishable_key']}
                rules={[{ required: true, message: 'Please enter Stripe publishable key' }]}
              >
                <Input placeholder="pk_test_..." />
              </Form.Item>

              <Form.Item
                label="Secret Key"
                name={['stripe', 'secret_key']}
                rules={[{ required: true, message: 'Please enter Stripe secret key' }]}
              >
                <Input.Password placeholder="sk_test_..." />
              </Form.Item>

              <Form.Item
                label="Webhook Secret"
                name={['stripe', 'webhook_secret']}
                rules={[{ required: true, message: 'Please enter webhook secret' }]}
              >
                <Input.Password placeholder="whsec_..." />
              </Form.Item>

              <div style={{ background: '#f0f9ff', padding: 12, borderRadius: 6, fontSize: 12 }}>
                <Text type="secondary">
                  Demo keys are pre-configured for testing. Replace with live keys in production.
                </Text>
              </div>
            </Card>
          </Col>

          {/* API Configuration */}
          <Col xs={24} lg={12}>
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ApiOutlined style={{ color: '#059669' }} />
                  <span>API Configuration</span>
                </div>
              }
              style={{ borderRadius: 14 }}
            >
              <Form.Item
                label="Default Rate Limit (calls per window)"
                name={['api', 'rate_limit_default']}
                rules={[{ required: true, message: 'Please enter rate limit' }]}
              >
                <InputNumber 
                  min={1} 
                  max={10000} 
                  style={{ width: '100%' }} 
                  placeholder="500"
                />
              </Form.Item>

              <Form.Item
                label="Rate Limit Window (minutes)"
                name={['api', 'rate_limit_window']}
                rules={[{ required: true, message: 'Please enter window duration' }]}
              >
                <InputNumber 
                  min={1} 
                  max={1440} 
                  style={{ width: '100%' }} 
                  placeholder="60"
                />
              </Form.Item>

              <Form.Item
                label="HMAC Authentication"
                name={['api', 'hmac_enabled']}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                label="CORS Origins"
                name={['api', 'cors_origins']}
                help="Comma-separated origins or * for all"
              >
                <Input placeholder="https://example.com, https://app.example.com" />
              </Form.Item>

              <Form.Item
                label="IP Whitelist"
                name={['api', 'ip_whitelist_enabled']}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Card>
          </Col>

          {/* Integration Templates */}
          <Col xs={24}>
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <GlobalOutlined style={{ color: '#7c3aed' }} />
                  <span>Third-Party Integrations</span>
                </div>
              }
              style={{ borderRadius: 14 }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Zoho Webhook URL"
                    name={['integrations', 'zoho_webhook_url']}
                    help="Webhook endpoint for Zoho CRM events"
                  >
                    <Input placeholder="https://your-domain.com/webhooks/zoho" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item
                    label="Trello Webhook URL"
                    name={['integrations', 'trello_webhook_url']}
                    help="Webhook endpoint for Trello board events"
                  >
                    <Input placeholder="https://your-domain.com/webhooks/trello" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item
                    label="Joomla Plugin"
                    name={['integrations', 'joomla_plugin_enabled']}
                    valuePropName="checked"
                    help="Enable Joomla plugin integration"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />
              
              <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8 }}>
                <Title level={5} style={{ margin: '0 0 12px 0' }}>Integration Status</Title>
                <Space direction="vertical" size="small">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag color="green">Zoho CRM</Tag>
                    <Text type="secondary">Ready for webhook configuration</Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag color="blue">Trello</Tag>
                    <Text type="secondary">API integration available</Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag color="purple">Joomla</Tag>
                    <Text type="secondary">Plugin template ready</Text>
                  </div>
                </Space>
              </div>
            </Card>
          </Col>

          {/* Security Settings */}
          <Col xs={24}>
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <SecurityScanOutlined style={{ color: '#dc2626' }} />
                  <span>Security & Monitoring</span>
                </div>
              }
              style={{ borderRadius: 14 }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <div style={{ background: '#fef2f2', padding: 16, borderRadius: 8, border: '1px solid #fecaca' }}>
                    <Title level={5} style={{ margin: '0 0 8px 0', color: '#dc2626' }}>Security Features</Title>
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      <li>API keys are hashed using bcrypt</li>
                      <li>HMAC signing with SHA256</li>
                      <li>Rate limiting per user/key</li>
                      <li>IP whitelist support</li>
                      <li>CORS protection</li>
                    </ul>
                  </div>
                </Col>

                <Col xs={24} md={12}>
                  <div style={{ background: '#f0f9ff', padding: 16, borderRadius: 8, border: '1px solid #bfdbfe' }}>
                    <Title level={5} style={{ margin: '0 0 8px 0', color: '#2563eb' }}>Monitoring</Title>
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      <li>Real-time API usage tracking</li>
                      <li>Rate limit breach alerts</li>
                      <li>Failed authentication logs</li>
                      <li>Quota usage notifications</li>
                      <li>Revenue analytics</li>
                    </ul>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <Button 
            type="primary" 
            htmlType="submit" 
            size="large"
            icon={<SaveOutlined />}
            loading={loading}
          >
            Save Platform Settings
          </Button>
        </div>
      </Form>

      {/* Generate API Key Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <KeyOutlined style={{ color: '#3730a3' }} />
            <span>Generate API Key for User</span>
          </div>
        }
        open={generateModalVisible}
        onCancel={closeGenerateModal}
        footer={null}
        width={600}
      >
        {!generatedKey ? (
          <div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                <UserOutlined style={{ marginRight: 4 }} />
                Select User
              </label>
              <Select
                showSearch
                placeholder="Search and select a user"
                style={{ width: '100%' }}
                value={selectedUser}
                onChange={setSelectedUser}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={users.map(user => ({
                  value: user.id,
                  label: `${user.name} (${user.email})`
                }))}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                <KeyOutlined style={{ marginRight: 4 }} />
                Key Name
              </label>
              <Input
                placeholder="e.g., Production API, Development Key"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={closeGenerateModal}>
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleGenerateKey}
                loading={apiKeysLoading}
                disabled={!selectedUser || !keyName}
              >
                Generate API Key
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ 
              background: '#fef3c7', 
              border: '1px solid #fbbf24', 
              padding: 16, 
              borderRadius: 8,
              marginBottom: 16
            }}>
              <div style={{ fontWeight: 600, marginBottom: 8, color: '#92400e' }}>
                ⚠️ Important: Save these credentials securely
              </div>
              <div style={{ fontSize: 12, color: '#78350f' }}>
                These credentials will only be shown once. Make sure to copy them now.
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>User</div>
              <div>{generatedKey.user_name} ({generatedKey.user_email})</div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>Key Name</div>
              <div>{generatedKey.key_name}</div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>API Key</div>
              <Input.TextArea
                value={generatedKey.api_key}
                readOnly
                rows={2}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
              <Button
                size="small"
                style={{ marginTop: 4 }}
                onClick={() => {
                  navigator.clipboard.writeText(generatedKey.api_key);
                  message.success('API key copied to clipboard');
                }}
              >
                Copy API Key
              </Button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>Signing Secret</div>
              <Input.TextArea
                value={generatedKey.signing_secret}
                readOnly
                rows={2}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
              <Button
                size="small"
                style={{ marginTop: 4 }}
                onClick={() => {
                  navigator.clipboard.writeText(generatedKey.signing_secret);
                  message.success('Signing secret copied to clipboard');
                }}
              >
                Copy Signing Secret
              </Button>
            </div>

            <div style={{ textAlign: 'right' }}>
              <Button type="primary" onClick={closeGenerateModal}>
                Done
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PlatformSettings;
